import { useState, useCallback } from 'react';

// Sanity bounds for stored width values. Anything outside these is treated as
// corruption — one bad entry once made the # column fill the whole table and
// blocked all resizing. The percentage sum is also checked because a single
// column dragged to e.g. 85% passes the per-column range but still wrecks the
// layout; defaults always sum to 100 so anything well off that is bad.
const MIN_PX_WIDTH  = 40;
const MAX_PX_WIDTH  = 800;
const MIN_PCT_WIDTH = 3;
const MAX_PCT_WIDTH = 70;
const MIN_PCT_SUM   = 85;
const MAX_PCT_SUM   = 115;

const isValidWidth = (n, min, max) =>
  typeof n === 'number' && Number.isFinite(n) && n >= min && n <= max;

/**
 * useResizableColumns — pixel-width drag-resizable columns with localStorage persistence.
 *
 * @param {string} storageKey  - localStorage key for persistence
 * @param {number[]} defaults  - default pixel widths, one per data column
 * @returns {{ template: string, widths: number[], startDrag: (colIndex: number, mouseEvent: MouseEvent) => void }}
 */
export function useResizableColumns(storageKey, defaults) {
  const [widths, setWidths] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (
          Array.isArray(parsed) &&
          parsed.length === defaults.length &&
          parsed.every(w => isValidWidth(w, MIN_PX_WIDTH, MAX_PX_WIDTH))
        ) {
          return parsed;
        }
        // Stored value is corrupt or out-of-range — wipe it so the user
        // doesn't get stuck with bad widths next session either.
        try { localStorage.removeItem(storageKey); } catch {}
      }
    } catch {
      try { localStorage.removeItem(storageKey); } catch {}
    }
    return [...defaults];
  });

  const template = widths.map(w => `${w}px`).join(' ');

  const startDrag = useCallback((colIndex, e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = widths[colIndex];

    const onMove = (mv) => {
      const newW = Math.max(60, startWidth + mv.clientX - startX);
      setWidths(prev => {
        const next = [...prev];
        next[colIndex] = newW;
        return next;
      });
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setWidths(prev => {
        try { localStorage.setItem(storageKey, JSON.stringify(prev)); } catch {}
        return prev;
      });
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [widths, storageKey]);

  return { template, widths, startDrag };
}

/**
 * useResizableTableColumns — percentage-width drag-resizable columns for HTML tables.
 *
 * Stores widths as percentages so they scale correctly regardless of table width.
 * Dragging column i redistributes space between column i and column i+1 (neighbour swap).
 * The drag handle sits on the right border of a <th>; there is no handle on the last column.
 *
 * @param {string} storageKey  - localStorage key for persistence
 * @param {number[]} defaultPcts - default percentage widths (should roughly sum to 96–100)
 * @returns {{ pcts: number[], startDrag: (colIndex: number, mouseEvent: MouseEvent) => void, reset: () => void }}
 */
export function useResizableTableColumns(storageKey, defaultPcts) {
  const [pcts, setPcts] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (
          Array.isArray(parsed) &&
          parsed.length === defaultPcts.length &&
          parsed.every(p => isValidWidth(p, MIN_PCT_WIDTH, MAX_PCT_WIDTH))
        ) {
          const sum = parsed.reduce((a, b) => a + b, 0);
          if (sum >= MIN_PCT_SUM && sum <= MAX_PCT_SUM) return parsed;
        }
        try { localStorage.removeItem(storageKey); } catch {}
      }
    } catch {
      try { localStorage.removeItem(storageKey); } catch {}
    }
    return [...defaultPcts];
  });

  const startDrag = useCallback((colIndex, e) => {
    e.preventDefault();
    e.stopPropagation();
    const table = e.target.closest('table');
    if (!table) return;
    const tableWidth = table.offsetWidth;
    if (!tableWidth) return;
    const startX     = e.clientX;
    const startLeft  = pcts[colIndex];
    const startRight = pcts[colIndex + 1];

    const onMove = (mv) => {
      const deltaPct = ((mv.clientX - startX) / tableWidth) * 100;
      const newLeft  = Math.max(5, startLeft  + deltaPct);
      const newRight = Math.max(5, startRight - deltaPct);
      setPcts(prev => {
        const next = [...prev];
        next[colIndex]     = newLeft;
        next[colIndex + 1] = newRight;
        return next;
      });
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setPcts(prev => {
        try { localStorage.setItem(storageKey, JSON.stringify(prev)); } catch {}
        return prev;
      });
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [pcts, storageKey]);

  const reset = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch {}
    setPcts([...defaultPcts]);
  }, [storageKey, defaultPcts]);

  return { pcts, startDrag, reset };
}
